import {
	isInFlight,
	type AvdDesktopRecord,
	type AvdDesktopStatus,
	type AvdPowerState,
	type BundleInventoryItem,
} from "@internal/backstage-plugin-avd-common";
import PlayCircleFilledIcon from "@material-ui/icons/PlayCircleFilled";
import PowerSettingsNewIcon from "@material-ui/icons/PowerSettingsNew";
import ScheduleIcon from "@material-ui/icons/Schedule";
import { ReactNode } from "react";

import type { BundleInventoryDescriptor } from "../admin/bundleDescriptors";
import type { HealthReason } from "../admin/bundleHealth";
import { DetailFact, FactIcon, FactList, absoluteTime } from "../admin/inventoryCells";

import { AVD_RESOURCE_GROUP, avdSubscriptionName, desiredIacCommitUrl } from "./desktopFacts";

// The AVD type's part of the bundle inventory: the desktop's own facts (power, estate drift, session
// host, subscription) layered on the generic row. Shapes: docs/app/avd-registry.md § The admin page.

/** The AVD facts the backend puts in a row's `details`. */
const record = (item: BundleInventoryItem): AvdDesktopRecord | undefined =>
	item.details as AvdDesktopRecord | undefined;

/** The power cell's words. `undefined` is an em-dash, never "off": unreadable is not switched off. */
export function powerLabel(power: AvdPowerState | undefined): string {
	switch (power) {
		case "running":
			return "Running";
		case "starting":
			return "Starting";
		case "stopping":
			return "Stopping";
		case "stopped":
			return "Stopped";
		case "deallocating":
			return "Shutting down";
		case "deallocated":
			return "Off";
		default:
			return "—";
	}
}

/** Live power. Off is the expected resting state (nightly auto-shutdown), so it is never an error. */
function powerCell(item: BundleInventoryItem): ReactNode {
	const power = record(item)?.azure?.power;
	const label = powerLabel(power);
	if (power === "running")
		return <FactIcon icon={PlayCircleFilledIcon} tone="ok" label={label} />;
	if (power === "starting" || power === "stopping" || power === "deallocating") {
		return <FactIcon icon={ScheduleIcon} tone="pending" label={label} />;
	}
	if (power === "stopped" || power === "deallocated") {
		return <FactIcon icon={PowerSettingsNewIcon} tone="aborted" label={label} />;
	}
	return null;
}

const isEstateDrifted = (item: BundleInventoryItem) => {
	const drift = record(item)?.drift;
	return drift === "missing-in-azure" || drift === "unregistered";
};

/** The desktop section of the detail sheet: the machine coordinates. */
function AvdDetails(props: Readonly<{ item: BundleInventoryItem }>) {
	const { item } = props;
	const desktop = record(item);
	if (!desktop) return null;
	return (
		<FactList>
			<DetailFact label="VM name" value={desktop.vmName} mono copy />
			<DetailFact label="Resource group" value={AVD_RESOURCE_GROUP} mono />
			<DetailFact
				label="Subscription"
				value={avdSubscriptionName(desktop.slug, desktop.subscriptionName)}
				mono
				copy
			/>
			<DetailFact label="Subscription id" value={desktop.subscriptionId} mono copy />
			<DetailFact label="Session host" value={desktop.azure?.sessionHostStatus} />
			<DetailFact
				label="Power"
				value={desktop.azure?.power ? powerLabel(desktop.azure.power) : undefined}
			/>
			<DetailFact
				label="Last run"
				value={desktop.runId ? `Run ${desktop.runId}` : undefined}
				href={desktop.runUrl}
			/>
			<DetailFact
				label="Desired from"
				value={desiredIacCommitUrl() ? "Commit this app was built from" : undefined}
				href={desiredIacCommitUrl()}
			/>
			<DetailFact label="First seen" value={absoluteTime(desktop.createdAt)} />
		</FactList>
	);
}

const AVD_BUCKETS = [
	{ id: "failed", label: "Failed" },
	{ id: "unconfirmed", label: "Unconfirmed" },
	{ id: "inFlight", label: "In flight" },
	{ id: "ready", label: "Ready" },
	{ id: "absent", label: "No desktop" },
];

export const avdInventoryDescriptor: BundleInventoryDescriptor = {
	bundleType: "avd",
	noun: "desktop",
	buckets: () => AVD_BUCKETS,
	matchesBucket: (bucket, item) => {
		switch (bucket) {
			case "failed":
			case "unconfirmed":
			case "ready":
				return item.status === bucket;
			case "inFlight":
				return isInFlight(item.status as AvdDesktopStatus);
			case "absent":
				// An `unregistered` row says absent because the registry has no row; Azure has the desktop.
				return item.status === "absent" && record(item)?.drift !== "unregistered";
			default:
				return false;
		}
	},
	renderState: powerCell,
	stateText: (item) => {
		const power = record(item)?.azure?.power;
		return power ? powerLabel(power) : undefined;
	},
	isEstateDrifted,
	runUrl: (item) => record(item)?.runUrl,
	// Teardown keeps the row's last version; no desktop, no version.
	hasNoBundle: (item) => item.status === "absent",
	searchText: (item) => {
		const desktop = record(item);
		return [
			desktop?.vmName,
			avdSubscriptionName(item.slug, desktop?.subscriptionName),
			desktop?.subscriptionId,
		]
			.filter(Boolean)
			.join(" ");
	},
	healthReasons: (item) => {
		const reasons: HealthReason[] = [];
		const drift = record(item)?.drift;
		if (drift === "unregistered") {
			reasons.push({
				id: "unregistered",
				label: "Unregistered",
				issue: "In Azure but not in the registry",
				why: "Azure has a desktop for this colleague that the bundle registry does not know about.",
				level: "attention",
			});
		} else if (item.status === "absent") {
			reasons.push({
				id: "no-desktop",
				label: "No desktop",
				issue: "Registry has no desktop",
				why: "The registry entry exists, but it records no desktop for this bundle.",
				level: "info",
			});
		}
		if (drift === "missing-in-azure") {
			reasons.push({
				id: "not-in-azure",
				label: "Not in Azure",
				issue: "Registered but not in Azure",
				why: "The registry has this desktop as ready, but no session host for it exists in Azure.",
				level: "attention",
			});
		}
		if (item.status === "failed") {
			reasons.push({
				id: "failed",
				label: "Failed",
				issue: "Last run failed",
				why: "The latest run did not finish cleanly. Open the run to see whether it was a setup or a removal.",
				level: "failed",
			});
		}
		if (isInFlight(item.status as AvdDesktopStatus)) {
			reasons.push({
				id: "in-flight",
				label: item.status === "deprovisioning" ? "Removing" : "Setting up",
				issue:
					item.status === "deprovisioning"
						? "Desktop being removed"
						: "Desktop being set up",
				why: "A run is in progress; the status settles when it finishes.",
				level: "info",
			});
		}
		if (item.status === "unconfirmed") {
			reasons.push({
				id: "unconfirmed",
				label: "Unconfirmed",
				issue: "Desktop not verified",
				why: "The run did not verify this desktop.",
				level: "attention",
			});
		}
		return reasons;
	},
	// A synthesised row has no registry row to hold.
	hasRegistryRow: (item) => record(item)?.drift !== "unregistered",
	Details: AvdDetails,
};
